import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Skeleton,
  cn,
} from '@parrot-software/design-system';
import type { ColumnDefinition } from '@reports/domain/value-objects/blocks/ColumnConfig';

interface TableBlockSkeletonProps {
  columnsConfig: ReadonlyMap<string, ColumnDefinition>;
  rows?: number;
}

/**
 * TableBlockSkeleton - Loading skeleton for table blocks
 *
 * Displays a skeleton loading state with configurable rows and columns based on
 * the block configuration. Uses Skeleton components to show loading placeholders
 * that match the actual table structure.
 *
 * @param {TableBlockSkeletonProps} props - Component properties
 * @param {ReadonlyMap<string, ColumnDefinition>} props.columnsConfig - Column configuration map from blockConfig
 * @param {number} [props.rows=5] - Number of skeleton rows to display
 * @returns {React.ReactElement} Skeleton loading component
 *
 * @example
 * <TableBlockSkeleton columnsConfig={blockConfig.columnsConfig} rows={7} />
 */
export const TableBlockSkeleton = ({ columnsConfig, rows = 5 }: TableBlockSkeletonProps) => {
  const columnsEntries = Array.from(columnsConfig.entries());

  return (
    <div className="overflow-x-auto" data-testid="table-block-skeleton">
      <Table className="w-full">
        <TableHeader>
          <TableRow
            className="bg-(--base-muted-muted) border-b border-(--base-border-border)"
            data-testid="table-header-row"
          >
            {columnsEntries.map(([key, definition]) => (
              <TableHead
                className="min-h-10 px-4 text-(--base-muted-muted-foreground) font-normal"
                key={key}
                style={{ width: definition.width }}
              >
                <div
                  className={cn('flex', {
                    'justify-start': definition.align === 'left',
                    'justify-center': definition.align === 'center',
                    'justify-end': definition.align === 'right',
                  })}
                >
                  <Skeleton className="h-4 w-[70%] bg-(--base-accent-bg-accent50)" />
                </div>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>

        <TableBody>
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <TableRow className="border-b border-(--base-border-border)" key={`row-${rowIndex}`}>
              {columnsEntries.map(([key, definition]) => (
                <TableCell className="px-4 py-2.5 text-foreground" key={`cell-${rowIndex}-${key}`}>
                  <div
                    className={cn('flex', {
                      'justify-start': definition.align === 'left',
                      'justify-center': definition.align === 'center',
                      'justify-end': definition.align === 'right',
                    })}
                  >
                    <Skeleton className="h-4 w-[70%] bg-(--base-accent-bg-accent50)" />
                  </div>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
